import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import {
  getRedirectResult,
  onAuthStateChanged,
  signInWithRedirect,
  signOut,
  type User,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions, googleProvider } from "./firebase";

type Role = "owner" | "admin" | "staff" | "viewer";

interface AuthState {
  user: User | null;
  role: Role | null;
  status: "loading" | "signed-out" | "authorizing" | "ready" | "denied";
  error: string | null;
  /**
   * The address that was turned away, kept so the screen can name it.
   *
   * This is the whole difference between a dead end and a fixable mistake. A
   * phone signed into three Google accounts offers all three in the chooser,
   * and "this account is not authorised" does not say which one was picked.
   */
  deniedEmail: string | null;
  signIn: () => Promise<void>;
  /** Sign out and immediately reopen the Google chooser. */
  useAnotherAccount: () => Promise<void>;
  signOutNow: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/** Reads the `code` off a Firebase error without asserting a shape. */
function codeOf(err: unknown): string {
  return err && typeof err === "object" && "code" in err
    ? String((err as { code: unknown }).code)
    : "";
}

/**
 * Why a sign-in failure gets a message per cause.
 *
 * Every failure used to print "ההתחברות נכשלה. נסו שוב." — one sentence for a
 * blocked window, a missing provider, an unauthorised domain and a dead network
 * alike. Retrying fixes exactly one of those, so the advice was wrong three
 * times out of four, and the code that would have identified which was thrown
 * away before anyone could read it.
 */
function signInMessage(code: string): string {
  switch (code) {
    case "auth/network-request-failed":
      return "אין חיבור לאינטרנט, או שהוא נקטע באמצע. בדקו את החיבור ונסו שוב.";
    case "auth/unauthorized-domain":
      return "הכתובת שממנה נכנסתם לא מאושרת ב-Firebase. זו תקלה בהגדרות, לא בחשבון שלכם.";
    case "auth/operation-not-allowed":
      return "התחברות עם Google לא מופעלת בפרויקט. זו תקלה בהגדרות, לא בחשבון שלכם.";
    case "auth/too-many-requests":
      return "יותר מדי ניסיונות. חכו דקה ונסו שוב.";
    case "auth/web-storage-unsupported":
      return "הדפדפן חוסם אחסון מקומי, ובלי זה אי אפשר להתחבר. כבו את הגלישה בסתר או את חסימת ה-cookies לאתר הזה.";
    default:
      return `לא הצלחנו להתחבר${code ? ` (${code})` : ""}. נסו שוב.`;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<Role | null>(null);
  const [status, setStatus] = useState<AuthState["status"]>("loading");
  const [error, setError] = useState<string | null>(null);
  const [deniedEmail, setDeniedEmail] = useState<string | null>(null);

  /**
   * Collect the outcome of a redirect that has just come back.
   *
   * `onAuthStateChanged` reports the *user*, so a redirect that succeeded needs
   * nothing from here. A redirect that **failed** reports through no other
   * channel at all: without this the browser returns from Google to a screen
   * that looks untouched, which is the failure mode this whole change exists to
   * end. A success is left to the listener below rather than handled twice.
   */
  useEffect(() => {
    getRedirectResult(auth).catch((err) => {
      setError(signInMessage(codeOf(err)));
    });
  }, []);

  useEffect(() => {
    return onAuthStateChanged(auth, async (nextUser) => {
      setUser(nextUser);
      if (!nextUser) {
        setRole(null);
        setStatus("signed-out");
        return;
      }

      setStatus("authorizing");
      try {
        const ensure = httpsCallable<void, { workspaceId: string; role: Role }>(
          functions,
          "ensureMembership",
        );
        const result = await ensure();
        setRole(result.data.role);
        setStatus("ready");
        setError(null);
        setDeniedEmail(null);
      } catch (err) {
        /**
         * Being refused is a state that has to survive on screen, and it used
         * to be a trap.
         *
         * Firebase keeps the session in the browser, so a refused account came
         * back on every reload: sign in, get turned away, reload, get turned
         * away again, with no sign-out anywhere on the sign-in screen. The
         * screen reported failure permanently and there was no way from inside
         * the app to try a different account — which is exactly how this was
         * reported, as "it always says the login failed".
         *
         * The session is left signed in on purpose rather than cleared here, so
         * the address can be named on screen; `useAnotherAccount` is the way
         * out.
         */
        const code = codeOf(err);
        const email = nextUser.email ?? null;
        setDeniedEmail(email);
        setRole(null);
        setStatus("denied");
        setError(
          code === "functions/permission-denied"
            ? null // the screen words this one itself, using deniedEmail
            : err && typeof err === "object" && "message" in err
              ? String((err as { message: unknown }).message)
              : "לא הצלחנו לבדוק את ההרשאות. נסו שוב.",
        );
      }
    });
  }, []);

  /**
   * Sign in by leaving the page, not by opening a window next to it.
   *
   * `signInWithPopup` was measured failing on the product owner's Android
   * Chrome in a way that looks like nothing happening at all: Google recorded
   * two successful sign-ins at 11:37, and the app made **zero** requests to any
   * function in the ninety minutes around them. The account was authenticated
   * and the page was never told, because the popup could not hand its result
   * back to the opener. Nothing on screen changed, so there was nothing to
   * report except "still broken".
   *
   * A redirect has no handoff to fail. The browser leaves for Google, comes
   * back to this origin with the credential, and `onAuthStateChanged` runs on a
   * fresh load. It is also what Firebase recommends for mobile web, and this
   * CRM is used from a phone.
   *
   * `authDomain` deliberately stays on `chbapark-crm-dev.firebaseapp.com`.
   * Moving it to the app's own origin would end the cross-origin storage
   * question entirely, but the OAuth client only authorises the handler on
   * `firebaseapp.com` — the `web.app` one comes back `redirect_uri_mismatch`,
   * verified against Google, so that change needs a console edit first and
   * would break every sign-in without it. See `TODO.md`.
   */
  async function signIn() {
    setError(null);
    setDeniedEmail(null);
    try {
      await signInWithRedirect(auth, googleProvider);
    } catch (err) {
      setError(signInMessage(codeOf(err)));
    }
  }

  async function useAnotherAccount() {
    // Sign out first: without it Google can hand back the same account and the
    // screen simply repeats itself, which is what being stuck felt like.
    await signOut(auth);
    setRole(null);
    setError(null);
    setDeniedEmail(null);
    await signIn();
  }

  async function signOutNow() {
    await signOut(auth);
    setError(null);
    setDeniedEmail(null);
  }

  return (
    <AuthContext.Provider
      value={{ user, role, status, error, deniedEmail, signIn, useAnotherAccount, signOutNow }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
